/**
 * useStationImages - Query hook for fetching station images
 * Fetches icon, banner, and gallery images for a station
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { getStationMediaImages } from '../../api/stationImages';
import { IStationImagesResponse } from '../../components/StationMedia/types';

export const STATION_IMAGES_QUERY_KEY = 'stationMediaImages';

interface UseStationImagesOptions
  extends Omit<
    UseQueryOptions<IStationImagesResponse, Error>,
    'queryKey' | 'queryFn'
  > {
  enabled?: boolean;
  includePendingReview?: boolean;
  includeRejected?: boolean;
}

/**
 * Hook to fetch all images (icon, banner, gallery) for a station
 *
 * @param stationId - The station/location UUID
 * @param options - React Query options + includePendingReview
 * @returns Query result with station images
 *
 * @example
 * const { data, isLoading, error } = useStationImages(stationId, { includePendingReview: true });
 * // data.icon - single confirmed icon image or null
 * // data.banner - single confirmed banner image or null
 * // data.gallery - array of gallery images
 * // data.pending?.icon - pending icon awaiting review
 * // data.pending?.banner - pending banner awaiting review
 */
const useStationImages = (
  stationId: string,
  options?: UseStationImagesOptions
) => {
  const { includePendingReview, includeRejected, ...queryOptions } = options || {};

  return useQuery<IStationImagesResponse, Error>({
    queryKey: [STATION_IMAGES_QUERY_KEY, stationId, { includePendingReview, includeRejected }],
    queryFn: () => getStationMediaImages(stationId, { includePendingReview, includeRejected }),
    enabled: Boolean(stationId) && queryOptions?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 1,
    ...queryOptions,
  });
};

export default useStationImages;
